import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { ArrowRight, Building2, TrendingUp, PlusCircle } from 'lucide-react';
import NewsletterSignup from '../components/NewsletterSignup';

const Home = () => {
  const options = [
    {
      title: 'Bestehende GmbH kaufen',
      description:
        'Finde eine passende Bestands-GmbH und starte schneller mit einer bereits eingetragenen Gesellschaft.',
      icon: Building2,
      href: '/marktplatz',
      button: 'Zum Marktplatz',
      variant: 'outline',
      buttonClass:
        'border-2 border-blue-600 text-blue-600 hover:bg-blue-50 text-base px-8',
    },
    {
      title: 'GmbH zum Verkauf anbieten',
      description:
        'Reiche deine Bestands-GmbH diskret ein. Wir prüfen die Angaben und bringen dich mit qualifizierten Interessenten zusammen.',
      icon: TrendingUp,
      href: '/seller-onboarding',
      button: 'Jetzt anbieten',
      variant: 'default',
      buttonClass:
        'bg-gradient-to-r from-orange-600 to-yellow-600 hover:from-orange-700 hover:to-yellow-700 text-white shadow-lg hover:shadow-xl transition-all text-base px-8',
    },
    {
      title: 'Neue GmbH gründen',
      description:
        'Starte direkt das Formular. Wir begleiten dich von der Vorbereitung bis zur Eintragung – strukturiert und transparent.',
      icon: PlusCircle,
      href: '/onboarding',
      button: 'Formular starten',
      variant: 'default',
      buttonClass:
        'bg-gradient-to-r from-blue-600 to-teal-600 hover:from-blue-700 hover:to-teal-700 text-white shadow-lg hover:shadow-xl transition-all text-base px-8',
    },
  ];

  return (
    <div className="min-h-screen">
      <Helmet>
        <title>Firmbay | Der Marktplatz für GmbHs - Kaufen, Verkaufen, Gründen</title>
        <meta name="description" content="Firmbay - Der Marktplatz für GmbHs. Bestehende GmbH kaufen, verkaufen oder neu gründen. Professionelle Begleitung bei allen gesellschaftsrechtlichen Vorgängen." />
      </Helmet>
      {/* Hero */}
      <section className="pt-32 pb-16 px-4 sm:px-6 lg:px-8 bg-gradient-to-b from-blue-50 to-white">
        <div className="max-w-7xl mx-auto">
          <div className="max-w-3xl mx-auto text-center">
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-gray-900 mb-6 leading-tight">
              Firmbay
              <span className="block mt-2 bg-gradient-to-r from-blue-600 to-teal-600 bg-clip-text text-transparent">
                Kaufen. Verkaufen. Gründen.
              </span>
            </h1>
            <p className="text-lg md:text-xl text-gray-600 mb-10 leading-relaxed">
              Wähle, was du vorhast – wir führen dich direkt zum passenden Prozess.
            </p>
          </div>

          {/* 3 Optionen */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-6">
            {options.map((opt) => {
              const Icon = opt.icon;
              return (
                <Card
                  key={opt.title}
                  className="border-2 border-gray-200 hover:border-blue-200 hover:shadow-lg transition-all h-full"
                >
                  <CardContent className="pt-6 h-full flex flex-col">
                    <div className="w-12 h-12 rounded-xl bg-white shadow-sm border border-gray-200 flex items-center justify-center mb-4">
                      <Icon className="w-6 h-6 text-blue-700" />
                    </div>
                    <h2 className="text-xl font-bold text-gray-900 mb-2">{opt.title}</h2>
                    <p className="text-sm text-gray-600 leading-relaxed mb-6 flex-1">{opt.description}</p>
                    <Link to={opt.href} className="inline-block w-full mt-auto">
                      <Button
                        size="lg"
                        variant={opt.variant}
                        className={`w-full justify-center ${opt.buttonClass}`}
                      >
                        {opt.button}
                        <ArrowRight className="ml-2 w-5 h-5" />
                      </Button>
                    </Link>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Newsletter Section */}
      <section className="py-14 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-4xl mx-auto">
          <NewsletterSignup />
        </div>
      </section>

      {/* Kleine Kontakt-Alternative */}
      <section className="py-14 px-4 sm:px-6 lg:px-8 bg-gray-50">
        <div className="max-w-4xl mx-auto text-center">
          <p className="text-gray-600 mb-6">
            Unsicher, welcher Weg der richtige ist? Schreib uns – wir helfen schnell weiter.
          </p>
          <Link to="/kontakt">
            <Button size="lg" className="bg-gray-900 hover:bg-gray-800 text-white text-base px-8">
              Kontakt aufnehmen
              <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default Home;
